import { useState, useEffect, useCallback, useRef } from 'react';
import { Permission, Coordinates } from './types';
import { checkBrowserPermission, getCurrentPosition } from './permission-utils';
import { createLogger } from "@/utils/logger";

const logger = createLogger('location:updater');

// New: Threshold for position change to be considered significant (in meters)
const POSITION_CHANGE_THRESHOLD = 10; 

// New: Helper function to calculate distance between coordinates (Haversine formula)
const getDistanceInMeters = (lat1: number, lon1: number, lat2: number, lon2: number) => {
  const R = 6371e3; // Earth radius in meters
  const φ1 = lat1 * Math.PI/180;
  const φ2 = lat2 * Math.PI/180;
  const Δφ = (lat2-lat1) * Math.PI/180;
  const Δλ = (lon2-lon1) * Math.PI/180;

  const a = Math.sin(Δφ/2) * Math.sin(Δφ/2) +
            Math.cos(φ1) * Math.cos(φ2) *
            Math.sin(Δλ/2) * Math.sin(Δλ/2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));

  return R * c;
};

// New: Check if position changed significantly
const hasPositionChangedSignificantly = (oldCoords: Coordinates, newCoords: {latitude: number, longitude: number}) => {
  if (!oldCoords) return true;
  
  const distance = getDistanceInMeters(
    oldCoords.lat, 
    oldCoords.lng, 
    newCoords.latitude, 
    newCoords.longitude
  );
  
  return distance > POSITION_CHANGE_THRESHOLD;
};

/**
 * Hook for updating location and handling permissions
 */
export const useLocationUpdater = (
  locationCore,
  promptsManager,
  config,
  toast
) => {
  const { locationState, setLocationState, saveLocation, clearLocation, isLocationStale } = locationCore;
  const { trackInteraction } = promptsManager;
  
  const requestDebounceRef = useRef<number | null>(null);
  const watchIdRef = useRef<number | null>(null);
  const lastWatchUpdateRef = useRef<number>(0); // Track the last watch update time

  // Wrap getCurrentPosition to update state properly
  const getCurrentPositionWrapped = useCallback((): Promise<GeolocationPosition> => {
    return getCurrentPosition(
      () => {
        setLocationState(prev => ({
          ...prev,
          isLoading: true,
          permissionTransitioning: true,
          error: null
        }));
      },
      () => {
        setLocationState(prev => ({
          ...prev,
          permissionTransitioning: false
        }));
      }
    );
  }, [setLocationState]);

  // Handle permission changes
  const handlePermissionChange = useCallback((state: Permission) => {
    // Only update if the permission actually changed
    if (state === locationState.permission) return;
    
    logger.debug(`Permission changed from ${locationState.permission} to ${state}`);
    
    setLocationState(prev => ({
      ...prev,
      permission: state,
      permissionTransitioning: false
    }));
    
    locationCore.setToStorage('locationPermission', state);
    
    if (state === 'granted') {
      updateLocation({ silent: true });
    } else if (state === 'denied') {
      clearLocation();
    }
  }, [locationState.permission, clearLocation, setLocationState, locationCore]);

  // Update location middleware
  const updateLocation = useCallback(async (options: {
    retries?: number, 
    silent?: boolean,
    force?: boolean 
  } = {}) => {
    const { retries = 3, silent = false, force = false } = options;
    
    // Avoid updates when not needed
    if (!force && !silent && !isLocationStale(config.staleTolerance) && locationState.coords) {
      logger.debug('Location is current, skipping update');
      return locationState.coords;
    }
    
    if (!silent) {
      setLocationState(prev => ({ 
        ...prev, 
        isLoading: true, 
        error: null,
        permissionTransitioning: true 
      }));
    }
    
    // Check browser permissions first
    const newPermission = await checkBrowserPermission();
    
    // Only update permission state if it changed
    if (newPermission !== locationState.permission) {
      handlePermissionChange(newPermission);
    }
    
    if (newPermission === 'denied') {
      setLocationState(prev => ({ 
        ...prev, 
        permission: 'denied',
        isLoading: false,
        permissionTransitioning: false,
        error: 'Location permission denied'
      }));
      
      clearLocation();
      return null;
    }
    
    // Use exponential backoff for retries
    let attempt = 0;
    while (attempt < retries) {
      try {
        const position = await getCurrentPositionWrapped();
        const coords = {
          lat: position.coords.latitude,
          lng: position.coords.longitude
        };
        
        saveLocation(coords);
        return coords;
      } catch (error) {
        logger.debug(`Error getting location (attempt ${attempt + 1}/${retries})`);
        attempt++;
        
        // Check if it's a permission error
        if ((error as GeolocationPositionError)?.code === 1) {
          setLocationState(prev => ({
            ...prev, 
            permission: 'denied',
            isLoading: false,
            permissionTransitioning: false,
            error: 'Location permission denied',
            coords: null
          }));
          
          handlePermissionChange('denied');
          clearLocation();
          return null;
        }
        
        // Only set error if all retries failed
        if (attempt >= retries) {
          const errorMessage = (error as GeolocationPositionError)?.message || 'Unable to get location';
          setLocationState(prev => ({ 
            ...prev, 
            isLoading: false,
            permissionTransitioning: false,
            error: errorMessage
          }));
        }
        
        // Exponential backoff
        if (attempt < retries) {
          await new Promise(r => setTimeout(r, 1000 * Math.pow(2, attempt)));
        }
      }
    }
    
    setLocationState(prev => ({ 
      ...prev, 
      isLoading: false,
      permissionTransitioning: false 
    }));
    
    return null;
  }, [
    locationState.permission,
    locationState.coords,
    isLocationStale,
    saveLocation,
    clearLocation,
    setLocationState,
    getCurrentPositionWrapped,
    handlePermissionChange,
    config.staleTolerance
  ]);

  // Request location with debouncing
  const requestLocation = useCallback(async () => {
    // Debounce rapid requests
    if (requestDebounceRef.current) {
      clearTimeout(requestDebounceRef.current);
      requestDebounceRef.current = null;
    }
    
    // Set a small delay to catch rapid multiple calls
    return new Promise<Coordinates | null>((resolve) => {
      requestDebounceRef.current = window.setTimeout(async () => {
        requestDebounceRef.current = null;
        try {
          setLocationState(prev => ({ 
            ...prev, 
            isLoading: true, 
            error: null,
            permissionTransitioning: true 
          }));
          
          const currentPermission = await checkBrowserPermission();
          
          if (currentPermission === 'denied') {
            setLocationState(prev => ({
              ...prev,
              permission: 'denied',
              isLoading: false,
              permissionTransitioning: false,
              error: 'Location permission denied'
            }));
            
            locationCore.setToStorage('locationPermission', 'denied');
            
            toast({
              title: "Location Access Denied",
              description: "Please enable it in your browser settings to use location features.",
              variant: "destructive",
              key: "location-denied"
            });
            
            resolve(null);
            return;
          }
    
          const position = await getCurrentPositionWrapped();
          const coords = {
            lat: position.coords.latitude,
            lng: position.coords.longitude
          };
    
          saveLocation(coords);
          trackInteraction('location_granted');
          resolve(coords);
        } catch (error) {
          logger.warn('Error getting location:', error);
          let errorMessage = (error as Error).message;
          
          if ((error as GeolocationPositionError)?.code === 1) {
            setLocationState(prev => ({
              ...prev,
              isLoading: false,
              error: 'Permission denied',
              permission: 'denied',
              permissionTransitioning: false
            }));
            
            locationCore.setToStorage('locationPermission', 'denied');
            errorMessage = "Location access denied. Please enable it in your browser settings.";
          } else {
            setLocationState(prev => ({ 
              ...prev, 
              isLoading: false,
              permissionTransitioning: false,
              error: errorMessage
            }));
          }
          
          toast({
            title: "Location Access Required",
            description: errorMessage,
            variant: "destructive",
            key: "location-error"
          });
          
          resolve(null);
        }
      }, 50);
    });
  }, [
    setLocationState,
    getCurrentPositionWrapped,
    saveLocation,
    trackInteraction,
    toast,
    locationCore
  ]);

  // Force refresh location
  const refreshLocation = useCallback(async () => {
    const currentPermission = await checkBrowserPermission();
    
    if (currentPermission !== locationState.permission) {
      handlePermissionChange(currentPermission);
    }
    
    if (currentPermission === 'granted') {
      return updateLocation({ force: true });
    } else if (currentPermission === 'prompt') {
      return requestLocation();
    }
    
    return null;
  }, [
    locationState.permission,
    handlePermissionChange,
    updateLocation,
    requestLocation
  ]);

  // Set up permission change monitoring
  useEffect(() => {
    if (navigator.permissions) {
      // Use explicit Promise typing and handling
      const permissionPromise = navigator.permissions.query({ name: 'geolocation' as PermissionName })
        .then((result) => {
          result.addEventListener('change', () => {
            handlePermissionChange(result.state as Permission);
          });
          return Promise.resolve();
        });
        
      // Add proper type handling for the Promise chain
      (permissionPromise as Promise<void>).catch((err) => {
        logger.debug('Error setting up permission listener:', err);
      });
    }
    
    return () => {
      // This is just a cleanup comment - the event listener cleanup
      // is handled by the browser when the permissions object is garbage collected
    };
  }, [handlePermissionChange]);

  // Set up position watching for active sessions
  useEffect(() => {
    let updateInterval: number | null = null;
    
    const setupLocationWatching = async () => {
      // First check the actual browser permission
      const currentPermission = await checkBrowserPermission();
      
      // Update state if different from what we have
      if (currentPermission !== locationState.permission) {
        handlePermissionChange(currentPermission);
      }
      
      // If permission is granted, get initial location and set up watch
      if (currentPermission === 'granted') {
        if (!locationState.coords || isLocationStale(config.staleTolerance)) {
          updateLocation({ silent: true });
        }
        
        // Set up position watching for active sessions
        if (navigator.geolocation && watchIdRef.current === null) {
          // Always clean up existing watches first
          if (watchIdRef.current !== null) {
            navigator.geolocation.clearWatch(watchIdRef.current);
            watchIdRef.current = null;
          }
          
          watchIdRef.current = navigator.geolocation.watchPosition(
            position => {
              // Throttle watch position updates
              const now = Date.now();
              if (now - lastWatchUpdateRef.current < 800) {
                // Skip updates that are too frequent
                return;
              }
              
              const newCoords = {
                lat: position.coords.latitude,
                lng: position.coords.longitude
              };
              
              // Only update if position changed significantly
              if (hasPositionChangedSignificantly(locationState.coords, position.coords)) {
                saveLocation(newCoords);
                lastWatchUpdateRef.current = now;
              }
            },
            error => {
              // Only handle permission denied errors
              if (error.code === 1) {
                handlePermissionChange('denied');
              }
            },
            { 
              enableHighAccuracy: true,
              timeout: 30000,
              maximumAge: 5 * 60 * 1000
            }
          );
        }
        
        // Set up periodic updates as a backup
        updateInterval = window.setInterval(() => {
          // Skip update if we're already loading or in transition
          if (locationState.isLoading || locationState.permissionTransitioning) {
            return;
          }
          
          if (isLocationStale(config.staleTolerance)) {
            updateLocation({ silent: true });
          }
        }, config.updateInterval);
      }
    };

    // Ensure proper promise handling with explicit Promise cast
    const watchPromise = setupLocationWatching();
    (watchPromise as Promise<void>).catch(err => {
      logger.warn('Error setting up location watching:', err);
    });
    
    // Cleanup function
    return () => {
      if (watchIdRef.current !== null && navigator.geolocation) {
        navigator.geolocation.clearWatch(watchIdRef.current);
        watchIdRef.current = null;
      }
      
      if (updateInterval !== null) {
        clearInterval(updateInterval);
      }
    };
  }, [
    locationState.permission,
    locationState.coords,
    locationState.isLoading,
    locationState.permissionTransitioning,
    handlePermissionChange,
    updateLocation,
    isLocationStale,
    saveLocation,
    config.updateInterval,
    config.staleTolerance
  ]);

  return {
    requestLocation,
    refreshLocation,
    updateLocation,
  };
};
